import { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { ApiError } from 'requests/RequestBase';
import { FormErrors } from 'validators/LoginValidator';
import { LoginParameters } from 'components/templates/LoginTemplate';
import { Store } from 'store/Store';
import {
	callCommonLoginV1Api,
	callCommonLoginV2Api,
	CommonLoginV1ResData,
	CommonLoginV2ResData,
	GetLoginDisplayItemResData,
	TermsRedirectResData,
} from 'requests/RequestLogin';
import { RoutePath } from 'consts/RoutePath';
import {
	convertObjectToGetParameter,
	convertObjectToGetParameterWithEncode,
} from 'common/ObjectToGetParameterConverter';
import { HttpStatus } from 'consts/HttpStatus';
import { findErrorMessageByApiError } from 'consts/ApiErrorCode';
import { ReactError } from 'consts/ReactError';

/**
 * 共通ログイン処理のカスタムフック
 * フォームバリデーション -> ログイン実処理 -> ページ遷移 を段階的に分けている
 * useStateが非同期なのでuseEffectを使って段階的にしないとエラーをキャッチすることができないため
 */
export const useCommonLogin = (
	formErrors: FormErrors,
	setFormErrors: React.Dispatch<React.SetStateAction<FormErrors>>,
	getErrorMessage: Function,
	loginParameters: LoginParameters,
	store: Store,
	actionError: string,
	setActionError: React.Dispatch<React.SetStateAction<string>>,
	isV1: boolean,
	setProcessLoading: React.Dispatch<React.SetStateAction<boolean>>,
	loginDisplayItem: GetLoginDisplayItemResData,
) => {
	const history = useHistory();
	// フォームバリデーション
	const [isFormValidationFinished, setIsFormValidationFinished] = useState(false);
	const login = async () => {
		setProcessLoading(true);

		Object.keys(formErrors).forEach((k) => {
			setFormErrors((prev) => ({ ...prev, [k]: getErrorMessage(k, loginParameters[k]) }));
		});
		setIsFormValidationFinished(true);
	};

	// ログイン実処理
	const [isLoginFinished, setIsLoginFinished] = useState(false);
	type ResponseData = {
		v1: CommonLoginV1ResData;
		v2: CommonLoginV2ResData;
	};
	const [resDatas, setResDatas] = useState<ResponseData>({ v1: undefined, v2: undefined });
	useEffect(() => {
		if (!isFormValidationFinished) {
			return;
		}

		const isError = !!Object.values(formErrors).find((e) => e !== '');
		if (isError) {
			setIsFormValidationFinished(false);
			setProcessLoading(false);
			return;
		}

		const executeLogin = async () => {
			setActionError('');
			if (isV1) {
				const result = await callCommonLoginV1Api({
					gid: loginParameters.gakkenId,
					pw: loginParameters.password,
					...store.authorizeV1Data,
				});
				if (result.status === HttpStatus.INTERNAL_SERVER_ERROR) {
					setActionError(ReactError.E9999);
					setIsLoginFinished(true);
					return;
				}
				if (result.status === HttpStatus.GATEWAY_TIMEOUT) {
					setActionError(ReactError.E9998);
					setIsLoginFinished(true);
					return;
				}
				if (result.status === HttpStatus.REDIRECT) {
					const termsData = result.data as TermsRedirectResData;
					const getParameters = convertObjectToGetParameterWithEncode(store.authorizeV1Data, 'burl');
					// パラメータはURLエンコードされている状態
					history.push(
						`${RoutePath.TERMS}?from=commonloginv1&isFirstAgreement=${termsData.isFirstAgreement}&${getParameters}`,
					);
					return;
				}
				if (result.status !== HttpStatus.OK) {
					const errorData = result.data as ApiError;
					setActionError(findErrorMessageByApiError(errorData));
					setIsLoginFinished(true);
					return;
				}
				const data = result.data as CommonLoginV1ResData;
				setResDatas((prev) => ({ ...prev, v1: data }));
			} else {
				const result = await callCommonLoginV2Api({
					gid: loginParameters.gakkenId,
					pw: loginParameters.password,
					...store.authorizeV2Data,
				});
				if (result.status === HttpStatus.INTERNAL_SERVER_ERROR) {
					setActionError(ReactError.E9999);
					setIsLoginFinished(true);
					return;
				}
				if (result.status === HttpStatus.GATEWAY_TIMEOUT) {
					setActionError(ReactError.E9998);
					setIsLoginFinished(true);
					return;
				}
				if (result.status === HttpStatus.REDIRECT) {
					const termsData = result.data as TermsRedirectResData;
					const getParameters = convertObjectToGetParameterWithEncode(
						store.authorizeV2Data,
						'code_challenge',
						'redirect_uri',
						'nonce',
					);
					if (termsData.type === 'terms') {
						// パラメータはURLエンコードされている状態
						history.push(
							`${RoutePath.TERMS}?from=commonloginv2&isFirstAgreement=${termsData.isFirstAgreement}&${getParameters}`,
						);
					} else if (termsData.type === 'required_profile') {
						history.push(`${RoutePath.REQUIRED_PROFILE_EDIT}?${getParameters}`);
					} else if (termsData.type === 'attribute') {
						history.push(`${RoutePath.ATTRIBUTE_AGREEMENT}?${getParameters}`);
					}
					return;
				}
				if (result.status !== HttpStatus.OK) {
					const errorData = result.data as ApiError;
					setActionError(findErrorMessageByApiError(errorData));
					setIsLoginFinished(true);
					return;
				}
				const data = result.data as CommonLoginV2ResData;
				setResDatas((prev) => ({ ...prev, v2: data }));
			}
			setIsLoginFinished(true);
		};
		executeLogin();
	}, [isFormValidationFinished]);

	// ページ遷移
	useEffect(() => {
		if (!isLoginFinished) {
			return;
		}

		const isError = actionError !== '';
		if (isError) {
			setIsFormValidationFinished(false);
			setIsLoginFinished(false);
			setProcessLoading(false);
			return;
		}

		const getParameters = isV1 ? convertObjectToGetParameter(resDatas.v1) : convertObjectToGetParameter(resDatas.v2);
		// リダイレクト先urlがエンコードされたままなのでデコードしてからリダイレクトする
		const redirectUrl = isV1
			? `${decodeURIComponent(store.authorizeV1Data.burl)}?${getParameters}`
			: `${
					store.authorizeV2Data.redirect_uri
						? decodeURIComponent(store.authorizeV2Data.redirect_uri)
						: loginDisplayItem.redirect_url
			  }?${getParameters}`;
		window.location.href = redirectUrl;
	}, [isLoginFinished]);

	return {
		login,
	};
};
